import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";

export const Navbar = ({ t, lang, setLang, solid = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const light = solid || scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const link = `text-xs uppercase tracking-[0.2em] transition-colors ${
    light ? "text-[#1A1A1A]/70 hover:text-[#1C3829]" : "text-[#F7F5F0]/80 hover:text-[#C29B62]"
  }`;

  const links = [
    { to: "/appartamenti", label: t.nav.apartments, testid: "nav-apartments-link" },
    { to: "/servizi", label: t.nav.amenities, testid: "nav-amenities-link" },
    { to: "/recensioni", label: t.nav.reviews, testid: "nav-reviews-link" },
    { to: "/storia", label: t.nav.history, testid: "nav-history-link" },
    { to: "/contatti", label: t.nav.contact, testid: "nav-contact-link" },
  ];

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-500 ${
        light ? "backdrop-blur-xl bg-[#F7F5F0]/80 border-b border-[#C29B62]/20" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className={`font-serif-display text-xl tracking-tight transition-colors ${light ? "text-[#1C3829]" : "text-[#F7F5F0]"}`}
        >
          Villa Navagero
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link key={l.to} data-testid={l.testid} to={l.to} className={link}>{l.label}</Link>
          ))}
          <Link
            data-testid="nav-booking-cta"
            to="/prenota"
            className="text-xs uppercase tracking-[0.2em] bg-[#1C3829] text-[#F7F5F0] px-5 py-2.5 hover:text-[#C29B62] transition-colors"
          >
            {t.nav.booking}
          </Link>
        </nav>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-xs uppercase tracking-widest" data-testid="language-switcher">
            <button
              data-testid="lang-it-button"
              onClick={() => setLang("it")}
              className={`px-2 py-1 transition-colors ${
                lang === "it"
                  ? light ? "text-[#1C3829] font-semibold" : "text-[#F7F5F0] font-semibold"
                  : light ? "text-[#1A1A1A]/40 hover:text-[#1C3829]" : "text-[#F7F5F0]/50 hover:text-[#F7F5F0]"
              }`}
            >
              IT
            </button>
            <span className="text-[#C29B62]">/</span>
            <button
              data-testid="lang-en-button"
              onClick={() => setLang("en")}
              className={`px-2 py-1 transition-colors ${
                lang === "en"
                  ? light ? "text-[#1C3829] font-semibold" : "text-[#F7F5F0] font-semibold"
                  : light ? "text-[#1A1A1A]/40 hover:text-[#1C3829]" : "text-[#F7F5F0]/50 hover:text-[#F7F5F0]"
              }`}
            >
              EN
            </button>
          </div>
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            className={`md:hidden relative z-50 p-2 transition-colors ${
              open ? "text-[#F7F5F0]" : light ? "text-[#1C3829]" : "text-[#F7F5F0]"
            }`}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden fixed inset-0 z-40 bg-[#101f17]/[0.98] backdrop-blur-md"
        >
          <nav className="h-full flex flex-col items-center justify-center gap-8 px-6">
            {links.map((l) => (
              <Link
                key={l.to}
                data-testid={`nav-mobile-${l.testid}`}
                to={l.to}
                onClick={() => setOpen(false)}
                className="font-serif-display text-3xl font-light text-[#F7F5F0] hover:text-[#C29B62] transition-colors"
              >
                {l.label}
              </Link>
            ))}
            <Link
              data-testid="nav-mobile-booking-cta"
              to="/prenota"
              onClick={() => setOpen(false)}
              className="mt-4 text-xs uppercase tracking-[0.25em] bg-[#C29B62] text-[#101f17] px-10 py-4 hover:bg-[#F7F5F0] transition-colors"
            >
              {t.nav.booking}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
